<template>
  <section
    class="section-layout"
    :class="[
      {
        'section-layout--large': large,
        'section-layout--imaged': bgWithImage,
        'section-layout--call-to-action': isCallToAction
      },
      bgThemeClass
    ]"
  >
    <header class="section-layout__header">
      <StepCounterList
        v-if="steps"
        class="section-layout__steps"
        :steps="steps"
        :current-step="currentStep"
      />

      <h2
        v-if="title.length"
        class="section-layout__title"
        v-html="title"
      />

      <div
        v-if="title.length"
        class="section-layout__subtitle"
      >
        {{ subtitle }}
      </div>
    </header>


    <div class="section-layout__wrapper">
      <div class="section-layout__content">
        <slot/>
      </div>

      <div v-if="$slots['aside-content']" class="section-layout__aside">
        <slot name="aside-content" />
      </div>
    </div>

    <slot name="actions" />
  </section>
</template>

<script>
import StepCounterList from '@/components/shared/steps-counters/StepCounterList.vue';

const BG_THEMES = ['transparent', 'primary-invert', 'primary'];

export default {
  name: 'section-layout',
  components: {
    StepCounterList,
  },
  props: {
    title: {
      type: String,
      required: false,
      default: '',
    },
    subtitle: {
      type: String,
      required: false,
      default: '',
    },
    bgTheme: {
      type: String,
      required: false,
      default: 'transparent',
      validator: function (theme) {
        return theme ? BG_THEMES.includes(theme) : true
      }
    },
    bgWithImage: {
      type: Boolean,
      required: false,
      default: false
    },
    large: {
      type: Boolean,
      required: false,
      default: false
    },
    isCallToAction: {
      type: Boolean,
      required: false,
      default: false
    },
    steps: {
      type: Number,
      required: false,
      default: 0
    },
    currentStep: {
      type: Number,
      required: true,
      default: 0,
    },
  },
  computed: {
    bgThemeClass() {
      return `section-layout--bg-${this.bgTheme}`;
    },
  },
}
</script>

<style lang="scss" scoped>
.section-layout {
  position: relative;

  $border-radius-desktop: $border-radius-medium;

  padding: rem($gap-large);

  border-radius: rem($border-radius-desktop);

  @media #{$screen-big} {
    padding: rem($gap-big);
  }

  @media (#{$screen-small}) {
    padding: rem($gap-medium) rem($gap-normal);
  }

  &--large {
    $gap-large: 68px;
    $gap-big: 60px;
    $vertical-padding-tablet: 40px;

    padding: rem($gap-large) 0;

    @media #{$screen-big} {
      padding: rem($gap-big) 0;
    }

    @media #{$screen-medium} {
      padding: rem(24px) 0 $vertical-padding-tablet;
    }

    @media #{$screen-small} {
      padding-bottom: rem(45px);
    }
  }

  &--bg-transparent {
    background-color: transparent;
  }

  &--bg-primary {
    background-color: $primary;
  }

  &--bg-primary#{&}--imaged {
    background: linear-gradient(0deg, rgba($primary, 0.9) 0%, rgba($primary, 0.90) 100%), url('@/assets/images/hands-with-pen.png'), $alto 50%;
    background-repeat: no-repeat;
    background-size: cover;
  }

  &--bg-primary-invert {
    background-color: $primary-invert;
  }

  &--imaged {
    background-size: cover;
    background-repeat: no-repeat;
  }

  &--call-to-action {
    @media #{$screen-medium} {
      padding: rem($gap-medium);
    }
  }

  &__header {
    &:not(:last-child) {
      margin-bottom: rem(32);
    }
  }

  &__wrapper {
    position: relative;

    display: flex;
    gap: rem(40);

    @media #{$screen-big} {
      flex-direction: column;
    }

    &:not(:last-child) {
      margin-bottom: rem(32);
    }
  }

  &__content {
    flex-grow: 1;
  }

  &__aside {
    top: rem(16);
    position: sticky;

    flex-shrink: 0;
    align-self: flex-start;

    width: percentage(421 / 1104);

    @media #{$screen-big} {
      width: 100%;
    }
  }

  &__title {
    @include text-xxl;

    @media #{$screen-big} {
      font-size: rem($font-size-huge);
    }

    @media #{$screen-small} {
      font-size: rem($font-size-large);
    }

    &:not(:last-child) {
      margin-bottom: rem(8);
    }
  }

  &__subtitle {
    @include text-props(18, 24);
  }

  &__steps {
    position: absolute;
    top: rem(48);
    right: rem(48);

    @media #{$screen-big} {
      top: rem(32);
      right: rem(32);
    }

    @media #{$screen-small} {
      position: static;
      top: rem(24);
      left: rem(12);
    }

    &:not(:last-child) {
      margin: 0;

      @media #{$screen-small} {
        margin-bottom: rem(24);
      }
    }
  }
}
</style>
